import React, { useState } from 'react';
import {
  FileText,
  Plus,
  Trash2,
  Check,
  X,
  Sparkles,
  ExternalLink,
  ChevronRight,
  Edit2,
  Globe,
} from 'lucide-react';
import { useSite } from '../../context/SiteContext';

interface MobilePageSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenNewPageModal: () => void;
}

export const MobilePageSheet: React.FC<MobilePageSheetProps> = ({
  isOpen,
  onClose,
  onOpenNewPageModal,
}) => {
  const { activeSite, activePageId, setActivePageId, updatePageInSite, deletePageFromSite } = useSite();
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editSlug, setEditSlug] = useState('');

  if (!isOpen || !activeSite) return null;

  const handleStartEdit = (page: any) => {
    setEditingPageId(page.id);
    setEditTitle(page.title);
    setEditSlug(page.slug);
  };

  const handleSaveEdit = (pageId: string) => {
    updatePageInSite(pageId, {
      title: editTitle,
      slug: editSlug.startsWith('/') ? editSlug : `/${editSlug}`,
    });
    setEditingPageId(null);
  };

  return (
    <div className="fixed inset-0 z-50 lg:hidden flex flex-col justify-end">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs"
        onClick={onClose}
      />

      <div className="relative z-10 w-full bg-slate-900 border-t border-slate-700 rounded-t-3xl shadow-2xl flex flex-col max-h-[85dvh] text-slate-100 select-none">
        {/* Header */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/60 rounded-t-3xl">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-blue-600/20 text-blue-400 flex items-center justify-center">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Sayfa Yönetimi</h3>
              <p className="text-[11px] text-slate-400">Toplam {(activeSite.pages || []).length} sayfa</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                onClose();
                onOpenNewPageModal();
              }}
              className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center gap-1 shadow-sm transition"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Yeni Sayfa</span>
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Page List */}
        <div className="p-4 overflow-y-auto space-y-2.5 flex-1 custom-scrollbar">
          {(activeSite.pages || []).map((page) => {
            const isActive = page.id === activePageId;
            const isEditing = editingPageId === page.id;

            return (
              <div
                key={page.id}
                className={`p-3 rounded-2xl border transition ${
                  isActive
                    ? 'bg-slate-800/90 border-blue-500 shadow-md ring-1 ring-blue-500/30'
                    : 'bg-slate-800/40 border-slate-700/60 hover:bg-slate-800/70'
                }`}
              >
                {isEditing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Sayfa Başlığı</label>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-white px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">URL Slug</label>
                      <input
                        type="text"
                        value={editSlug}
                        onChange={(e) => setEditSlug(e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 text-xs font-mono text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                      />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-1">
                      <button
                        onClick={() => setEditingPageId(null)}
                        className="px-3 py-1.5 rounded-lg bg-slate-800 text-xs text-slate-400 font-semibold"
                      >
                        İptal
                      </button>
                      <button
                        onClick={() => handleSaveEdit(page.id)}
                        className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-xs text-white font-bold"
                      >
                        Kaydet
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div
                      onClick={() => {
                        setActivePageId(page.id);
                        onClose();
                      }}
                      className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                    >
                      <div
                        className={`w-3 h-3 rounded-full shrink-0 ${
                          isActive ? 'bg-blue-500 ring-4 ring-blue-500/20' : 'bg-slate-600'
                        }`}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white truncate">{page.title}</span>
                          {page.isHomePage && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] font-black">
                              Ana Sayfa
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 font-mono block truncate">{page.slug}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => handleStartEdit(page)}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition"
                        title="Düzenle"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>

                      {!page.isHomePage && (
                        <button
                          onClick={() => deletePageFromSite(page.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition"
                          title="Sil"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setActivePageId(page.id);
                          onClose();
                        }}
                        className={`p-1.5 rounded-lg transition ${
                          isActive ? 'text-blue-400' : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-slate-800 bg-slate-950/80 flex justify-end pb-safe">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold transition"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};
